<template>
  <div>
    <h4 v-if="this.currentState === this.states.FETCHING_STUDENT" class="text-center">
      Loading student information...
    </h4>

    <h4 v-if="this.currentState === this.states.FETCHING_STATEMENTS" class="text-center">
      Generating statements...
    </h4>

    <h3 v-if="this.currentState === this.states.STUDENT_ERROR" class="text-center bg-danger">
      Failed to load student information! Please wait a moment and refresh this page.
    </h3>

    <h3 v-if="this.currentState === this.states.STATEMENT_ERROR" class="text-center bg-danger">
      Failed to generate statement information! Please wait a moment and refresh this page.
    </h3>

    <template v-if="this.currentState === this.states.DONE">
      <h2 v-if="studentName && subject === 'Behavior'">
        Describe the specific
        <span class="subject">Behavior</span>
        that is most relevant to
        <span class="student-name fs-block">{{ studentName }}'s</span> present levels.
      </h2>
      <h2 v-else-if="subject">
        Identify Area(s) of Strength or Need
        <br >
        <template v-if="studentName">
          for
          <span class="student-name fs-block">{{ studentName }}</span>
        </template>
        in
        <span class="subject">{{ currentSubjectLabel }}</span>
      </h2>
      <br >

      <skill-statements
        v-for="skill in skills"
        :key="skill.skill_uuid"
        :subject="currentSubject"
        :skill="skill"
        :student-name="studentName"
        :student-grade="studentGrade"
        :student-statements="skill.studentStatements"
        :present-level-uuid="uuid"
        :locked="skill.locked"
      />

      <template v-if="impacts.length > 0 && (iodVisibility || effectsVisibility)">
        <h2 class="impact-heading">
          <span v-if="!effectsVisibility">Select Impact of Disability Statement(s)</span>
          <span v-else>Effect Statement</span>
          <br >
          <template v-if="studentName">
            for
            <span class="student-name fs-block">{{ studentName }}</span>
          </template>
          in
          <span class="subject">{{ currentSubjectLabel }}</span>
        </h2>
        <br >

        <skill-statements
          v-for="impact in impacts"
          :key="impact.skill_uuid"
          :subject="currentSubject"
          :skill="impact"
          :skill-type="skillTypes.IMPACT"
          :student-name="studentName"
          :student-grade="studentGrade"
          :student-statements="impact.studentStatements"
          :present-level-uuid="uuid"
          :multi-select="true"
          :use-effects-template="effectsVisibility"
          :locked="impact.locked"
        />
      </template>

      <div class="action-button-container text-center">
        <button
          v-if="uuid"
          id="plDraftButton"
          class="btn btn-primary"
          @click="gotoConclusion">
          <template v-if="nextSubject">Select {{ nextSubjectLabel }} Statements</template>

          <template v-else>Generate draft present levels</template>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import {
  getPresentLevelStudentStatement,
  getSkillBySubject,
  getImpactBySubject,
  getAreasOfNeed,
  getSkillTypes,
  returnGradeFromEnrolledGrade,
  getSkillForEffectStatement,
} from '@/api/presentlevels'
import { mapGetters, mapMutations } from 'vuex'
import { authorizationCheckMixin } from '@/router/authorization_mixin'
import SkillStatements from '@/components/presentlevels/SkillStatements'

const States = Object.freeze({
  INIT: Symbol('Init'),
  FETCHING_STUDENT: Symbol('FetchingStudent'),
  FETCHING_STATEMENTS: Symbol('FetchingStatements'),
  STUDENT_ERROR: Symbol('StudentError'),
  STATEMENT_ERROR: Symbol('StatementError'),
  DONE: Symbol('Fetched'),
})

export default {
  name: 'PresentlevelsResults',
  components: {
    SkillStatements,
  },
  mixins: [authorizationCheckMixin],
  props: {
    uuid: {
      type: String,
      default: undefined,
    },
    subject: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      states: States,
      currentState: States.INIT,
      skillTypes: getSkillTypes(),

      currentSubject: '',
      nextSubject: '',
      selectedSubjects: [],
      studentName: '',
      studentGrade: '',
      grade: 'Pre-K',
      skills: [],
      impacts: [],
      studentStatements: [],
    }
  },
  computed: {
    ...mapGetters('visibility', ['selVisibility', 'iodVisibility', 'effectsVisibility']),
    ...mapGetters('user', ['licenseType', 'licenseSubType']),
    ...mapGetters('contentProfile', ['profileId']),
    currentSubjectLabel() {
      let aon = getAreasOfNeed({ selBlocked: !this.selVisibility }).filter((aon) => {
        return aon.value === this.currentSubject
      })

      return aon.length === 1 ? aon[0].label : this.currentSubject
    },
    nextSubjectLabel() {
      const nextSubject = getAreasOfNeed({ selBlocked: !this.selVisibility }).find(
        (area) => area.value === this.nextSubject
      )
      return nextSubject.label || this.nextSubject
    },
  },
  eventContext() {
    return {
      plw_area_of_need: this.currentSubject,
    }
  },
  watch: {
    $route(to, from) {
      this.init()
    },
  },
  methods: {
    ...mapMutations('presentlevels', [
      'setBreadcrumbInfo',
      'setLastUpdate',
      'setEnrolledGrade',
      'setAreasOfNeed',
      'setStudentName',
    ]),
    gotoConclusion() {
      if (this.nextSubject) {
        this.$sendEvent('Clicked Select Next Subject Statements')
        this.$router.push({
          name: 'PresentlevelsSubjectResults',
          params: { uuid: this.uuid, subject: this.nextSubject },
        })
      } else {
        this.$sendEvent('Clicked Generate Draft Present Levels', {
          statement_uuid: this.uuid,
          enrolled_grade: this.studentGrade,
          area_of_need: this.currentSubject,
        })

        this.$router.push({ name: 'PresentlevelsConclusion', params: { uuid: this.uuid } })
      }
    },
    async init() {
      let skillPromise
      this.currentState = States.INIT

      if (this.subject) {
        this.currentSubject = this.subject.charAt(0).toUpperCase() + this.subject.substring(1)
      }

      // If there is a UUID, then we need to grab student statement information
      // Otherwise we are in browse mode which requires a subject
      if (this.uuid) {
        this.currentState = States.FETCHING_STUDENT

        // There can be multiple subjects for a student.
        // If one is provided then fetch the skill statements for that subject
        // Otherwise, let the backend decide then replace the current history frame with a URI that includes the subject
        skillPromise = getPresentLevelStudentStatement(this.uuid, this.currentSubject)
        try {
          let data = await skillPromise
          await this.redirectIfUnauthorized(data.student.user_uuid, {
            name: 'PresentLevelsNewWizard',
          })

          this.currentState = States.FETCHING_STATEMENTS

          if (!this.currentSubject) {
            this.currentSubject = data.subject
            this.$router.replace({
              name: 'PresentlevelsSubjectResults',
              params: { uuid: this.uuid, subject: data.subject },
            })
          }
          this.selectedSubjects = getAreasOfNeed()
            .filter((subject) => data.student.selected_subjects.includes(subject.value))
            .map((subject) => subject.value)
          let currentSubjectIndex = this.selectedSubjects.indexOf(this.currentSubject)
          if (currentSubjectIndex >= 0 && currentSubjectIndex < this.selectedSubjects.length - 1) {
            this.nextSubject = this.selectedSubjects[currentSubjectIndex + 1]
          } else {
            this.nextSubject = ''
          }

          this.studentGrade = data.student.enrolled_grade_level
          this.studentName = data.student.student_name
          this.studentStatements = data.statements
          this.setLastUpdate(data.student.updated_at)
          this.setBreadcrumbInfo({
            uuid: this.uuid,
            availableStages: this.selectedSubjects,
          })
          this.setEnrolledGrade(this.studentGrade)
          this.setStudentName(this.studentName)
          this.setAreasOfNeed(this.selectedSubjects)
          if (this.effectsVisibility) {
            skillPromise = Promise.all([
              getSkillBySubject(data.subject, this.licenseSubType, this.profileId),
              getSkillForEffectStatement(data.subject, returnGradeFromEnrolledGrade(data.student.enrolled_grade_level)),
            ])
          } else {
            skillPromise = Promise.all([
              getSkillBySubject(data.subject, this.licenseSubType, this.profileId),
              getImpactBySubject(data.subject, this.licenseSubType, this.profileId),
            ])
          }
        } catch (err) {
          this.currentState = States.STUDENT_ERROR
          console.log(err)
          skillPromise = null
        }
      } else {
        if (!this.currentSubject) {
          this.currentSubject = 'Reading'
        }

        this.currentState = States.FETCHING_STATEMENTS
        this.$router.replace({
          name: 'PresentlevelsBrowse',
          params: { subject: this.currentSubject },
        })
        skillPromise = Promise.all([
          getSkillBySubject(this.currentSubject, this.licenseSubType, this.profileId),
          getImpactBySubject(this.currentSubject, this.licenseSubType, this.profileId),
        ])
      }

      if (skillPromise) {
        try {
          let data = await skillPromise
          let skillData = data[0]
          let impactData = data[1]

          // Sort and set skill statement data
          skillData.skills.sort((a, b) => {
            return a.skill_order - b.skill_order
          })

          this.skills = skillData.skills.map((skill) => {
            skill.studentStatements = this.studentStatements.filter(
              (stmnt) => stmnt.skill_uuid === skill.skill_uuid
            )

            return skill
          })

          // Sort and set impact statement data
          impactData.skills.sort((a, b) => {
            return a.skill_order - b.skill_order
          })

          this.impacts = impactData.skills.map((impact) => {
            impact.studentStatements = this.studentStatements.filter(
              (stmnt) => stmnt.skill_uuid === impact.skill_uuid
            )

            return impact
          })

          this.currentState = States.DONE
        } catch (err) {
          this.currentState = States.STATEMENT_ERROR
          console.error(err)
        }
      }
    },
  },
  mounted() {
    this.init()
  },
}
</script>

<style scoped lang="scss">
h2 {
  line-height: 36px;
  text-align: center;

  .subject {
    font-style: italic;
    color: #3c9ad5;
  }

  .student-name {
    font-style: italic;
    color: #3c9ad5;
  }
}

.impact-heading {
  margin-top: 40px;
}

#plDraftButton {
  margin-top: 50px;
  padding: 15px 25px;
  font-size: 26px;
}
</style>
